import { Link } from '../../components/base/Link';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { apiContactHref, contactEmail, damayaUrl, pricingCopyValues } from '../../seo/pricing';

export default function Pricing() {
  const { t } = useTranslation();
  const copy = (key: string) => t(`pricing.${key}`, pricingCopyValues);
  const button = 'inline-flex justify-center rounded-lg px-6 py-3 font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-600';

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <section className="bg-gradient-to-br from-slate-900 to-blue-900 px-6 py-20 text-white">
          <div className="mx-auto max-w-4xl text-center">
            <p className="mb-5 text-sm font-semibold tracking-wide text-cyan-300">{copy('status')}</p>
            <h1 className="mb-6 text-4xl font-bold leading-tight md:text-5xl">{copy('heroTitle')}</h1>
            <p className="text-lg leading-relaxed text-blue-100">{copy('heroSubtitle')}</p>
            <p className="mt-5 text-sm leading-relaxed text-blue-200">{copy('statusNote')}</p>
          </div>
        </section>

        <section className="px-6 py-16" aria-label={copy('plansLabel')}>
          <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-3">
            <article className="flex flex-col rounded-2xl border border-slate-200 p-8">
              <p className="mb-3 text-sm font-semibold text-blue-700">{copy('trialBadge')}</p>
              <h2 className="text-2xl font-bold text-slate-900">{copy('trialTitle')}</h2>
              <p className="my-6 text-4xl font-bold text-slate-900">{copy('trialPrice')}</p>
              <p className="mb-6 leading-relaxed text-slate-600">{copy('trialDesc')}</p>
              <ul className="mb-8 space-y-3 text-slate-700">
                {['trialQuota', 'trialEligibility', 'trialScope'].map(key => <li key={key}>{copy(key)}</li>)}
              </ul>
              <a href={apiContactHref} className={`${button} mt-auto bg-blue-50 text-blue-700 hover:bg-blue-100`}>{copy('applyTrial')}</a>
            </article>

            <article className="flex flex-col rounded-2xl border-2 border-blue-600 bg-blue-50/50 p-8">
              <p className="mb-3 text-sm font-semibold text-blue-700">{copy('standardBadge')}</p>
              <h2 className="text-2xl font-bold text-slate-900">{copy('standardTitle')}</h2>
              <dl className="my-6 space-y-4">
                <div><dt className="mb-1 text-sm text-slate-600">{copy('imageLabel')}</dt><dd className="text-3xl font-bold text-slate-900">¥{pricingCopyValues.imagePrice}<span className="ml-2 text-base font-normal">{copy('perImage')}</span></dd></div>
                <div><dt className="mb-1 text-sm text-slate-600">{copy('videoLabel')}</dt><dd className="text-3xl font-bold text-slate-900">¥{pricingCopyValues.videoPrice}<span className="ml-2 text-base font-normal">{copy('perMinute')}</span></dd></div>
              </dl>
              <p className="mb-6 font-medium text-blue-800">{copy('startingBalance')}</p>
              <ul className="mb-8 space-y-3 text-slate-700">
                {['standardBilling', 'standardDelivery', 'standardFailure'].map(key => <li key={key}>{copy(key)}</li>)}
              </ul>
              <a href={apiContactHref} className={`${button} mt-auto bg-blue-600 text-white hover:bg-blue-700`}>{copy('applyStandard')}</a>
            </article>

            <article className="flex flex-col rounded-2xl border border-slate-200 p-8">
              <p className="mb-3 text-sm font-semibold text-slate-500">{copy('customBadge')}</p>
              <h2 className="text-2xl font-bold text-slate-900">{copy('customTitle')}</h2>
              <p className="my-6 text-3xl font-bold text-slate-900">{copy('customPrice')}</p>
              <p className="mb-6 leading-relaxed text-slate-600">{copy('customDesc')}</p>
              <ul className="mb-8 space-y-3 text-slate-700">
                {['customVolume', 'customCapacity', 'customBoundary'].map(key => <li key={key}>{copy(key)}</li>)}
              </ul>
              <a href={apiContactHref} className={`${button} mt-auto bg-slate-100 text-slate-800 hover:bg-slate-200`}>{copy('contact')}</a>
            </article>
          </div>
          <div className="mx-auto mt-8 flex max-w-7xl flex-col gap-4 rounded-xl bg-slate-50 p-6 sm:flex-row sm:items-center sm:justify-between">
            <div><h2 className="font-semibold text-slate-900">{copy('damayaTitle')}</h2><p className="mt-2 text-sm text-slate-600">{copy('damayaDesc')}</p></div>
            <a href={damayaUrl} className={`${button} shrink-0 bg-white text-blue-700 ring-1 ring-slate-200 hover:bg-blue-50`}>{copy('damayaAction')}</a>
          </div>
        </section>

        <section id="scope" className="scroll-mt-24 bg-slate-50 px-6 py-16">
          <div className="mx-auto max-w-7xl">
            <h2 className="mb-8 text-3xl font-bold text-slate-900">{copy('scopeTitle')}</h2>
            <div className="grid gap-6 md:grid-cols-2">
              {['imageScope', 'videoScope', 'billingScope', 'deliveryScope'].map(key => (
                <article key={key} className="rounded-xl bg-white p-6">
                  <h3 className="mb-3 text-lg font-semibold text-slate-900">{copy(`${key}Title`)}</h3>
                  <p className="leading-relaxed text-slate-600">{copy(key)}</p>
                </article>
              ))}
            </div>
            <aside className="mt-8 rounded-xl border border-blue-200 bg-blue-50 p-6">
              <h3 className="mb-3 text-lg font-semibold text-blue-900">{copy('exampleTitle')}</h3>
              <p className="leading-relaxed text-blue-900">{copy('exampleDesc')}</p>
              <p className="mt-3 text-sm leading-relaxed text-blue-800">{copy('exampleNote')}</p>
            </aside>
          </div>
        </section>

        <section id="api-pilot" className="scroll-mt-24 px-6 py-16">
          <div className="mx-auto max-w-7xl">
            <h2 className="mb-8 text-3xl font-bold text-slate-900">{copy('processTitle')}</h2>
            <ol className="grid gap-6 md:grid-cols-3">
              {['sample', 'integration', 'production'].map((key, index) => (
                <li key={key} className="rounded-xl border border-slate-200 p-6">
                  <span className="mb-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 font-bold text-blue-700">{index + 1}</span>
                  <h3 className="mb-3 text-lg font-semibold text-slate-900">{copy(`${key}Title`)}</h3>
                  <p className="leading-relaxed text-slate-600">{copy(key)}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="faq" className="scroll-mt-24 bg-slate-50 px-6 py-16">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-8 text-3xl font-bold text-slate-900">{copy('faqTitle')}</h2>
            <div className="space-y-4">
              {['prepay', 'failure', 'formats', 'retention', 'support', 'damaya'].map(key => (
                <details key={key} className="rounded-xl border border-slate-200 bg-white p-6">
                  <summary className="cursor-pointer font-semibold text-slate-900">{copy(`faq.${key}.question`)}</summary>
                  <p className="mt-4 leading-relaxed text-slate-600">{copy(`faq.${key}.answer`)}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="scroll-mt-24 bg-blue-900 px-6 py-16 text-white">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="mb-4 text-3xl font-bold">{copy('contactTitle')}</h2>
            <p className="mb-6 leading-relaxed text-blue-100">{copy('contactDesc')}</p>
            <a href={apiContactHref} className={`${button} bg-white text-blue-900 hover:bg-blue-50`}>{copy('sendApplication')}</a>
            <p className="mt-5 text-sm text-blue-100">{copy('emailAlternative')} <a className="underline" href={`mailto:${contactEmail}`}>{contactEmail}</a></p>
            <Link to="/about" className="mt-5 inline-block text-sm text-blue-100 underline">{copy('aboutLink')}</Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
